import { useEffect } from 'react';
import { pickLocale, type TextCharacteristic } from '../../schema';
import { useCharacteristicSelection, useStoreActions } from '../../state/StoreContext';
import { useComponentSettings } from '../../state/ComponentContext';
import { formatBound } from '../../i18n/formatBound';
import { useTranslation } from '../../i18n';
import { inputTypeForValidator } from '../../validation/validate';

interface TextViewProps {
    characteristic: TextCharacteristic;
    locale: string | undefined;
}

export function TextView({ characteristic, locale }: TextViewProps) {
    const { t } = useTranslation();
    const settings = useComponentSettings();
    const selection = useCharacteristicSelection(characteristic.id);
    const { setText } = useStoreActions();
    const defaultText = pickLocale(characteristic.defaultValue, locale) ?? '';
    // Legacy `placeholder: "1"` means: show defaultValue as the HTML
    // placeholder and start the field empty. Otherwise the defaultValue
    // is the initial input value. Mirrors storage.js:419-425.
    const useAsPlaceholder = characteristic.useDefaultAsPlaceholder && defaultText !== '';
    const initialValue = useAsPlaceholder ? '' : defaultText;
    const current = selection?.kind === 'text' ? selection.value : initialValue;

    // The store starts empty; a non-empty defaultValue only lives in the
    // <input value=…> above. Without seeding the store, the required-
    // validator reads no selection and flags "Pflichtfeld" even though the
    // input visibly shows the default. Push the initial value into the
    // store once on mount so what the user sees is what the validator
    // sees. Skips when useAsPlaceholder is on (default is only a hint,
    // field is meant to start empty) or when a selection is already
    // present (snapshot restore, hot navigation).
    useEffect(() => {
        if (selection === undefined && initialValue !== '') {
            setText(characteristic.id, initialValue);
        }
        // Run only on mount / id change — the seed is a one-shot.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [characteristic.id]);

    // Map legacy `validate-email` / `validate-url` / `validate-phone` to the
    // matching HTML5 input type. Falls back to `text` for unknown.
    const inputType = inputTypeForValidator(characteristic.textValidate);
    const isNumeric = inputType === 'number';

    const minLen = numericOrUndef(characteristic.minStringLength);
    const maxLen = numericOrUndef(characteristic.maxStringLength);
    const min = numericOrUndef(characteristic.minValue);
    const max = numericOrUndef(characteristic.maxValue);

    // Component-level "$min - $max"-style hint. Mirrors legacy
    // option/abstract.js:renderMinMaxPattern: only fires when showMinMax is
    // on AND at least one bound exists AND a pattern is configured AND the
    // field is actually numeric. Cloud/admin emits "0"/"0" as the unset
    // sentinel for text fields — we don't surface that as "min 0 · max 0".
    const minMaxHint =
        isNumeric && settings.showMinMax && settings.minMaxPattern && (min !== undefined || max !== undefined)
            ? settings.minMaxPattern
                  .replace('$min', formatBound(characteristic.minValue, locale))
                  .replace('$max', formatBound(characteristic.maxValue, locale))
            : '';

    const constraints: string[] = [];
    if (isNumeric) {
        if (minMaxHint) {
            constraints.push(minMaxHint);
        } else {
            if (min !== undefined) constraints.push(t('text.min', { value: formatBound(characteristic.minValue, locale) }));
            if (max !== undefined) constraints.push(t('text.max', { value: formatBound(characteristic.maxValue, locale) }));
        }
    }
    // textValidate is a legacy named-validator key (`validate-email`, …),
    // not a user-visible string. It's reflected through the input type
    // and the ActionButton's validation pass — never rendered as raw text.

    return (
        <>
            <input
                type={inputType}
                className="cfg-input"
                value={current}
                placeholder={useAsPlaceholder ? defaultText : ''}
                required={characteristic.isRequired}
                minLength={!isNumeric ? minLen : undefined}
                maxLength={!isNumeric ? maxLen : undefined}
                min={isNumeric ? min : undefined}
                max={isNumeric ? max : undefined}
                step={
                    isNumeric && characteristic.decimalPlace > 0
                        ? Number(`1e-${characteristic.decimalPlace}`)
                        : isNumeric
                          ? 1
                          : undefined
                }
                onChange={(e) => setText(characteristic.id, e.target.value)}
            />
            {constraints.length > 0 && (
                <p className="cfg-char__info">{constraints.join(' · ')}</p>
            )}
        </>
    );
}

function numericOrUndef(v: string | undefined): number | undefined {
    if (v === undefined || v === '') return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
}
